import { Pencil, Plug, Plus, RefreshCw, RotateCcw, Tags, Trash2, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { useConnections, useDeleteConnection, useIntegrations, useSyncConnection, useUpdateConnection } from '@/api/hooks';
import type { Connection, Integration } from '@/api/types';
import { ConfirmDialog, Dialog } from '@/components/Dialog';
import { Menu, type MenuItem } from '@/components/Menu';
import { useToast } from '@/components/toast-context';
import { Button, ButtonLink, Card, EmptyState, ErrorNote, IntegrationIcon, PageHeader, Skeleton, TextField } from '@/components/ui';
import { useConnectFlow } from '@/features/connect/useConnectFlow';
import { DemoBadge } from '@/features/integrations/status';
import { formatDateTime, relativeTime } from '@/lib/format';
import { ConnectionStatusBadge } from './ConnectionStatus';

type Editing = { kind: 'rename' | 'symbols'; connection: Connection } | null;

function EditDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const update = useUpdateConnection();
  const toast = useToast();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<string | null>(null);
  const key = editing ? `${editing.kind}:${editing.connection.id}` : null;
  if (key !== seeded) {
    setSeeded(key);
    setValue(editing ? (editing.kind === 'rename' ? editing.connection.display_name : (editing.connection.fields?.symbols ?? '')) : '');
    setError(null);
  }
  const formId = 'edit-connection';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    if (!value.trim()) {
      setError(editing.kind === 'rename' ? 'Give it a name.' : 'Add at least one symbol.');
      return;
    }
    try {
      await update.mutateAsync(
        editing.kind === 'rename'
          ? { id: editing.connection.id, display_name: value.trim() }
          : { id: editing.connection.id, fields: { symbols: value.trim() } },
      );
      toast({ title: editing.kind === 'rename' ? 'Renamed' : 'Watchlist updated' });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? (err.fields.display_name ?? err.fields.symbols ?? err.message) : errorMessage(err));
    }
  }

  return (
    <Dialog
      open={!!editing}
      onClose={onClose}
      size="sm"
      title={editing?.kind === 'symbols' ? 'Edit ticker symbols' : 'Rename connection'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={update.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate>
        <TextField
          label={editing?.kind === 'symbols' ? 'Ticker symbols' : 'Name'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={error}
          autoFocus
          hint={editing?.kind === 'symbols' ? 'Separate with commas, e.g. AAPL, MSFT, SHOP.TO' : 'Shown on your cards, e.g. “@mystorage.ca”.'}
          spellCheck={false}
        />
      </form>
    </Dialog>
  );
}

function ConnectionRow({
  connection: c,
  integration,
  onEdit,
  onDisconnect,
}: {
  connection: Connection;
  integration?: Integration;
  onEdit: (e: Editing) => void;
  onDisconnect: (c: Connection) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const sync = useSyncConnection();
  const { connect, busy } = useConnectFlow('/connections');
  const name = integration?.name ?? c.integration_id;

  async function syncNow() {
    try {
      await sync.mutateAsync(c.id);
      toast({ tone: 'info', title: `Refreshing ${c.display_name}`, description: 'New numbers arrive in a moment.' });
    } catch (e) {
      toast({
        tone: isApiError(e, 429) ? 'info' : 'error',
        title: isApiError(e, 429) ? 'Already up to date' : `Couldn’t refresh ${c.display_name}`,
        description: errorMessage(e),
      });
    }
  }

  const items: MenuItem[] = [
    { label: 'Rename', icon: <Pencil className="size-4" aria-hidden />, onSelect: () => onEdit({ kind: 'rename', connection: c }) },
  ];
  if (integration?.auth === 'oauth2')
    items.push({
      label: `Change ${integration.account_label.toLowerCase()}`,
      icon: <Users className="size-4" aria-hidden />,
      onSelect: () => navigate(`/connections/${c.id}/account?next=/connections`),
    });
  if (c.integration_id === 'stocks')
    items.push({ label: 'Edit symbols', icon: <Tags className="size-4" aria-hidden />, onSelect: () => onEdit({ kind: 'symbols', connection: c }) });
  if (integration?.auth === 'oauth2')
    items.push({
      label: 'Reconnect',
      icon: <RotateCcw className="size-4" aria-hidden />,
      onSelect: () => void connect(integration, c.fields && Object.keys(c.fields).length ? { fields: c.fields } : undefined),
    });
  items.push({ label: 'Disconnect', icon: <Trash2 className="size-4" aria-hidden />, danger: true, onSelect: () => onDisconnect(c) });

  return (
    <Card as="li" className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <IntegrationIcon name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate font-medium">{c.display_name}</h2>
            <ConnectionStatusBadge status={c.status} />
            {c.is_demo && <DemoBadge />}
          </div>
          <p className="truncate text-sm text-muted-ink">
            {name}
            {c.external_account_name && c.external_account_name !== c.display_name ? ` · ${c.external_account_name}` : ''}
            {c.fields?.symbols ? ` · ${c.fields.symbols}` : ''}
          </p>
          <p className="text-xs text-muted-ink">
            <span title={formatDateTime(c.last_synced_at)}>Last synced {relativeTime(c.last_synced_at)}</span>
          </p>
          {c.last_error && (c.status === 'error' || c.status === 'reauth_required') && <p className="mt-1 text-xs text-down">{c.last_error}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 sm:shrink-0">
        {c.status === 'reauth_required' && integration ? (
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            onClick={() => void connect(integration, c.fields && Object.keys(c.fields).length ? { fields: c.fields } : undefined)}
          >
            Reconnect
          </Button>
        ) : c.status === 'needs_account' ? (
          <ButtonLink size="sm" variant="primary" to={`/connections/${c.id}/account?next=/connections`}>
            Choose {integration?.account_label.toLowerCase() ?? 'account'}
          </ButtonLink>
        ) : (
          <Button
            size="sm"
            onClick={() => void syncNow()}
            loading={sync.isPending}
            disabled={c.status === 'syncing'}
            icon={<RefreshCw className="size-4" aria-hidden />}
          >
            Sync now
          </Button>
        )}
        <Menu label={`More actions for ${c.display_name}`} items={items} />
      </div>
    </Card>
  );
}

export function ConnectionsPage() {
  const connections = useConnections();
  const integrations = useIntegrations();
  const remove = useDeleteConnection();
  const toast = useToast();
  const [editing, setEditing] = useState<Editing>(null);
  const [disconnecting, setDisconnecting] = useState<Connection | null>(null);
  const byId = new Map(integrations.data?.integrations.map((i) => [i.id, i]) ?? []);

  return (
    <div>
      <PageHeader
        title="Connections"
        description="The apps MyDash reads from. Numbers refresh about every hour."
        actions={
          <ButtonLink to="/integrations" variant="primary" icon={<Plus className="size-4" aria-hidden />}>
            Connect an app
          </ButtonLink>
        }
      />
      {connections.isError && <ErrorNote message={errorMessage(connections.error)} onRetry={() => void connections.refetch()} />}
      {connections.isPending && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}
      {connections.data?.length === 0 && (
        <EmptyState
          icon={<Plug className="size-5" aria-hidden />}
          title="Nothing connected yet"
          actions={
            <ButtonLink to="/integrations" variant="primary">
              Browse integrations
            </ButtonLink>
          }
        >
          Connect an app to see its numbers on your dashboard.
        </EmptyState>
      )}
      {connections.data && connections.data.length > 0 && (
        <ul className="flex flex-col gap-3">
          {connections.data.map((c) => (
            <ConnectionRow key={c.id} connection={c} integration={byId.get(c.integration_id)} onEdit={setEditing} onDisconnect={setDisconnecting} />
          ))}
        </ul>
      )}

      <EditDialog editing={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!disconnecting}
        onClose={() => setDisconnecting(null)}
        title={`Disconnect ${disconnecting?.display_name ?? ''}?`}
        confirmLabel="Disconnect"
        loading={remove.isPending}
        onConfirm={() => {
          if (!disconnecting) return;
          const c = disconnecting;
          remove.mutate(c.id, {
            onSuccess: () => {
              toast({ title: `${c.display_name} disconnected` });
              setDisconnecting(null);
            },
            onError: (e) => toast({ tone: 'error', title: 'Couldn’t disconnect', description: errorMessage(e) }),
          });
        }}
      >
        <p>Its cards, alerts and stored numbers are deleted, and MyDash stops reading from it. Updates that include it will skip it.</p>
      </ConfirmDialog>
    </div>
  );
}
